import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMyWordsQuery, type MyWord, type MyWordsKind } from '@entities/Word';
import { trackEvent } from '@shared/analytics';
import { buildUnitPath } from '@shared/config';
import { cn } from '@shared/lib';
import { ChevronRightIcon, PlainField, SearchIcon } from '@shared/ui';
import { tableColumns, WordLine } from './WordLine';
import { WordSheet } from './WordSheet';

const KINDS: readonly MyWordsKind[] = ['all', 'new', 'repeat'];
/** Подсказка «нажми на слово» — пока ученик ни разу не открыл слово на этом устройстве. */
const HINT_KEY = 'lx.words.hint';

function readHintSeen(): boolean {
  try {
    return localStorage.getItem(HINT_KEY) === '1';
  } catch {
    return false;
  }
}

function writeHintSeen(): void {
  try {
    localStorage.setItem(HINT_KEY, '1');
  } catch {
    // Приватное окно: подсказка просто покажется ещё раз.
  }
}

export interface WordsSectionProps {
  isMobile: boolean;
  /** Над строками — записи сегодняшних нажатий («Сегодня»): их рисует страница. */
  top?: ReactNode;
  /** Подсвеченные строки — добавленное сегодня и ещё не тренированное (user_card_state.id). */
  freshIds: ReadonlySet<string>;
  /** Выбранный слева (на телефоне — на полке) модуль: показаны только его слова (владелец 2026-10-28). */
  module: { id: string; title: string } | null;
  /** Компьютер: поиск и выбранный модуль — в плашке над колонками (владелец 2026-10-28: «как в кабине самолёта»),
   *  здесь — найденное по этому запросу, без своего поля и строки модуля. */
  externalQuery?: string;
}

/**
 * «Слова» на «Словах и модулях» (вариант B, 2026-10-28): все слова ученика, последние изменения сверху — только что
 * созданное встаёт первым и подсвечено. Строка — коротко (слово, переводы, пометка, модуль), нажатие открывает
 * подробности в шторке. Поиск — по слову и переводу, чипы «Все / Новые / Повторить» со счётчиками.
 */
export function WordsSection({ isMobile, top, freshIds, module, externalQuery }: WordsSectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const titleId = useId();
  const [kind, setKind] = useState<MyWordsKind>('all');
  const [searchOpen, setSearchOpen] = useState(false);
  const [input, setInput] = useState('');
  const [ownQ, setOwnQ] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setOwnQ(input.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [input]);
  const inToolbar = externalQuery !== undefined;
  const q = inToolbar ? externalQuery.trim() : ownQ;

  const query = useMyWordsQuery(q, kind, module?.id ?? null);
  const words = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data]);
  const counts = query.data?.pages[0]?.counts;
  const filtered = q !== '' || kind !== 'all' || !!module;
  const noWordsAtAll = !!counts && counts.all === 0 && !filtered;
  // Выбранный модуль пуст (не поиск и не фильтр) — сказать про модуль, а не «ничего не нашли».
  const moduleEmpty = !!module && !!counts && counts.all === 0 && q === '' && kind === 'all';
  const showTools = !!counts && (counts.all > 0 || filtered);

  const [opened, setOpened] = useState<MyWord | null>(null);
  const [hintSeen, setHintSeen] = useState(readHintSeen);
  const open = useCallback(
    (word: MyWord) => {
      setOpened(word);
      trackEvent('words_word_opened', { training: word.training, surface: isMobile ? 'phone' : 'desktop' });
      if (!hintSeen) {
        writeHintSeen();
        setHintSeen(true);
      }
    },
    [hintSeen, isMobile],
  );

  const pickKind = (next: MyWordsKind) => {
    if (next === kind) return;
    setKind(next);
    trackEvent('words_filter_changed', { kind: next });
  };
  const closeSearch = () => {
    setSearchOpen(false);
    setInput('');
  };
  // На компьютере поле поиска видно сразу; на телефоне — по кнопке «Найти», чтобы не съедать первый экран.
  const searchVisible = showTools && (!isMobile || searchOpen);

  const searchField = (
    <PlainField
      value={input}
      onChange={setInput}
      autoFocus={isMobile}
      placeholder={t('words.list.search')}
      ariaLabel={t('words.list.search')}
      maxLength={64}
      enterKeyHint="search"
      className="min-w-0 flex-1 rounded-pill border border-line bg-card px-4 py-2 text-[16px] leading-6 text-fg outline-none transition-colors focus:border-accent"
    />
  );

  return (
    <section aria-labelledby={titleId}>
      <div className="flex min-h-[44px] items-center justify-between gap-3">
        <h2 id={titleId} className="text-[13px] font-semibold uppercase tracking-[0.12em] text-muted">
          {t('words.list.title')}
          {counts && counts.all > 0 && <span className="tabular-nums">{` · ${counts.all}`}</span>}
        </h2>
        {showTools && !isMobile && !inToolbar && <div className="flex w-full max-w-[320px]">{searchField}</div>}
        {showTools && isMobile && !searchOpen && (
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="-mr-2 inline-flex min-h-[44px] items-center gap-1.5 rounded-pill px-2 text-[14px] font-semibold text-accent-hi outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <SearchIcon aria-hidden className="h-4 w-4" />
            {t('words.list.find')}
          </button>
        )}
      </div>
      {module && !inToolbar && (
        // Слова выбранного модуля: его имя и путь на страницу модуля.
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex h-8 max-w-full items-center rounded-pill bg-accent/[0.14] px-3 text-[13px] font-semibold text-accent-hi">
            <span className="min-w-0 truncate">{module.title}</span>
          </span>
          <button
            type="button"
            onClick={() => {
              trackEvent('words_module_page_opened');
              navigate(buildUnitPath(module.id));
            }}
            className="inline-flex min-h-[36px] items-center gap-0.5 text-[14px] font-semibold text-accent-hi outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {t('words.list.openModule')}
            <ChevronRightIcon aria-hidden className="h-4 w-4" />
          </button>
        </div>
      )}
      {searchVisible && isMobile && (
        <div className="mt-2 flex items-center gap-2">
          {searchField}
          <button
            type="button"
            onClick={closeSearch}
            className="min-h-[44px] flex-none rounded-pill px-2 text-[14px] font-medium text-muted hover:text-fg"
          >
            {t('words.list.cancel')}
          </button>
        </div>
      )}

      {showTools && counts && (
        // Чипы — одной строкой: на узком экране листаются вбок, а не переносятся.
        <div
          role="radiogroup"
          aria-label={t('words.list.filterLabel')}
          className="-mx-1 mt-2 flex gap-1.5 overflow-x-auto px-1 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {KINDS.map((id) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={kind === id}
              onClick={() => pickKind(id)}
              className={cn(
                'inline-flex h-8 flex-none items-center gap-1.5 whitespace-nowrap rounded-pill border px-3 text-[13px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-accent',
                kind === id
                  ? 'border-fg bg-fg font-semibold text-fg-inv'
                  : 'border-line bg-card text-[#cfcac3] hover:text-fg',
              )}
            >
              {t(`words.list.kinds.${id}`)}
              <span className={cn('tabular-nums', kind === id ? 'text-[#6a6a70]' : 'text-muted')}>{counts[id]}</span>
            </button>
          ))}
        </div>
      )}

      {top}

      {!hintSeen && words.length > 0 && (
        <p className="mt-2 px-3 text-[13px] leading-[18px] text-muted">{t('words.list.hint')}</p>
      )}

      {query.isPending ? (
        <ul aria-hidden className="mt-2 flex flex-col gap-1">
          {Array.from({ length: 5 }).map((_, index) => (
            <li key={index} className="h-[60px] animate-skeleton rounded-[14px] bg-card" />
          ))}
        </ul>
      ) : query.isError && !query.data ? (
        <div role="alert" className="mt-3 flex flex-wrap items-center gap-x-3 px-3">
          <p className="text-[14px] leading-5 text-muted">{t('words.list.loadError')}</p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="min-h-[44px] text-[14px] font-semibold text-accent-hi"
          >
            {t('words.list.retry')}
          </button>
        </div>
      ) : noWordsAtAll ? (
        <p className="mt-2 rounded-[18px] border border-dashed border-line px-4 py-5 text-[15px] leading-relaxed text-muted">
          {t('words.list.empty')}
        </p>
      ) : words.length === 0 ? (
        <p className="mt-2 px-3 py-6 text-center text-[15px] text-muted">
          {moduleEmpty ? t('words.list.moduleEmpty') : t('words.list.noMatch')}
        </p>
      ) : (
        <>
          {!isMobile && (
            // Заголовки колонок — по сетке строк (как в наброске владельца).
            <div
              aria-hidden
              className={cn(
                'mt-2 grid gap-4 border-b border-solid border-line px-3 pb-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-muted',
                tableColumns(!module),
              )}
            >
              <span>{t('words.list.cols.word')}</span>
              <span>{t('words.list.cols.translation')}</span>
              {!module && <span>{t('words.list.cols.module')}</span>}
              <span>{t('words.list.cols.status')}</span>
              <span />
            </div>
          )}
          <ul className={cn('mt-1 flex flex-col', query.isPlaceholderData && 'opacity-60 transition-opacity')}>
            {words.map((word) => (
              <WordLine
                key={word.stateId}
                word={word}
                fresh={freshIds.has(word.stateId)}
                layout={isMobile ? 'stacked' : 'table'}
                showModule={!module}
                onOpen={open}
              />
            ))}
          </ul>
        </>
      )}

      {query.hasNextPage && (
        <button
          type="button"
          onClick={() => {
            if (!query.isFetchingNextPage) void query.fetchNextPage();
          }}
          aria-busy={query.isFetchingNextPage || undefined}
          className="mt-2 inline-flex min-h-[44px] items-center px-3 text-[14px] font-semibold text-accent-hi"
        >
          {query.isFetchingNextPage ? t('words.list.loading') : t('words.list.more')}
        </button>
      )}

      <WordSheet word={opened} onClose={() => setOpened(null)} />
    </section>
  );
}
